// src/stores/files.store.ts
import { defineStore } from 'pinia'
import http from '@/libs/http'
import { ref } from 'vue'

export interface FileEntry {
	contentLength: number
	fileName: string
	fileId: string
	uploadTimestamp: number
}

export type FileNode = {
	name: string
	type: 'directory' | 'file'
	extension: string
	children: FileNode[]
	// only for directories:
	isOpen?: boolean
	isLoaded?: boolean
	isLoading?: boolean
	fullPath?: string
	isZipping?: boolean
	isDeleting?: boolean
	// only for files:
	fileId?: string
	contentLength?: number
	uploadTimestamp?: number
}

export interface IFileItem {
	fileId?: string
	name: string
	type: 'file' | 'directory'
	path: string
	uploadTimestamp?: number
	contentLength?: number
}

export type UploadStatus = 'queued' | 'uploading' | 'success' | 'error'

export interface FileUploadEntry {
	id: string
	name: string
	size: number
	status: UploadStatus
	progress: number
	target?: string
	error?: string
	uploadedAt?: number
}

export interface IListFilesParams {
	projectId?: string
	root?: string
	locationId?: string
	prefix?: string
}

export interface IZipFolderParams {
	fullPath: string
}

export interface IFolderSize {
	bytes: number
	files: number
}

export const useFilesStore = defineStore('files', () => {
	const previewFile = ref<FileNode | null>(null)
	const previewContext = ref<IListFilesParams | null>(null)

	const loading = ref<Record<string, boolean>>({})
	const uploads = ref<FileUploadEntry[]>([])

	// --- HELPERS ---
	function setLoading(key: string, val: boolean) {
		loading.value[key] = val
	}
	function formatError(error: any): string {
		return (
			error?.response?.data?.msg ||
			error?.response?.data ||
			error.message ||
			'An unexpected error occurred in hub store'
		)
	}
	async function withLoading<T>(key: string, fn: () => Promise<T>): Promise<T> {
		if (loading.value[key]) {
			throw new Error(`${key} is already in progress (hub)`)
		}
		try {
			setLoading(key, true)
			return await fn()
		} finally {
			setLoading(key, false)
		}
	}

	async function listFiles(params: IListFilesParams) {
		return withLoading('listFiles', async () => {
			try {
				let url = ''
				if (params.root) url = `/api/v2/storage/root/${btoa(params.root)}`
				else if (params.locationId) url = `/api/v2/storage/${params.projectId}/${params.locationId}`
				else url = `/api/v2/storage/${params.projectId}`
				const resp = await http.getAuth<FileEntry[]>(url)
				return resp
			} catch (error: any) {
				console.error('listFiles', error)
				throw new Error(formatError(error))
			}
		})
	}

	async function listFolder(params: IListFilesParams) {
		const { prefix, ...rest } = params
		const loadingKey = ['listFolder', rest.root, rest.projectId, rest.locationId, prefix || ''].filter(Boolean).join('-')
		return withLoading(loadingKey, async () => {
			try {
				const searchParams = new URLSearchParams()
				if (prefix) searchParams.set('prefix', prefix.replace(/^\/+/, '').replace(/\/+$/, ''))
				const query = searchParams.toString() ? `?${searchParams.toString()}` : ''

				let url = ''
				if (rest.root) url = `/api/v2/storage-tree/root/${btoa(rest.root)}${query}`
				else if (rest.locationId && rest.projectId) url = `/api/v2/storage-tree/${rest.projectId}/${rest.locationId}${query}`
				else if (rest.projectId) url = `/api/v2/storage-tree/${rest.projectId}${query}`
				else return []

				const resp = await http.getAuth<IFileItem[]>(url)
				return resp
			} catch (error: any) {
				console.error('listFolder', error)
				throw new Error(formatError(error))
			}
		})
	}

	async function zipFolder(params: IZipFolderParams) {
		const normalized = (params.fullPath || '').replace(/^\/+|\/+$/g, '')
		if (!normalized) throw new Error('Missing folder path')
		const parts = normalized.split('/').filter(Boolean)
		if (!parts.length) throw new Error('Missing folder path')

		const project = parts[0]
		let folderParts: string[] = []
		const headers: Record<string, string> = {}

		if (project === 'comapps') {
			const comappId = parts[1]
			if (!comappId) throw new Error('Missing ComApp id for zip')
			headers.comapp = ['comapps', comappId].join('/')
			folderParts = parts.slice(2)
		} else {
			folderParts = parts.slice(1)
		}

		if (!folderParts.length) throw new Error('Select a folder to zip')

		const foldername = `/${folderParts.join('/')}`
		headers.foldername = foldername

		return withLoading(`zipFolder-${normalized}`, async () => {
			try {
				const resp = await http.getAuth<{ url: string; filename?: string; status?: number; msg?: string }>(
					`/api/v2/zipfolder/${project}`,
					headers,
				)
				return resp
			} catch (error: any) {
				console.error('zipFolder', error)
				throw new Error(formatError(error))
			}
		})
	}

	async function folderSize(params: IZipFolderParams) {
		const normalized = (params.fullPath || '').replace(/^\/+|\/+$/g, '')
		if (!normalized) throw new Error('Missing folder path')
		const parts = normalized.split('/').filter(Boolean)
		if (!parts.length) throw new Error('Missing folder path')

		const project = parts[0]
		let folderParts: string[] = []
		const headers: Record<string, string> = {}

		if (project === 'comapps') {
			const comappId = parts[1]
			if (!comappId) throw new Error('Missing ComApp id for size')
			headers.comapp = ['comapps', comappId].join('/')
			folderParts = parts.slice(2)
		} else {
			folderParts = parts.slice(1)
		}

		if (!folderParts.length) throw new Error('Select a folder to measure')

		const foldername = `/${folderParts.join('/')}`
		headers.foldername = foldername

		return withLoading(`folderSize-${normalized}`, async () => {
			try {
				const resp = await http.getAuth<IFolderSize>(`/api/v2/storage-size/${project}`, headers)
				return resp
			} catch (error: any) {
				console.error('folderSize', error)
				throw new Error(formatError(error))
			}
		})
	}

	async function getFileUrl(fileKey: string, ctx: (IListFilesParams & { filePath?: string }) | null = null) {
		return withLoading(`getFileUrl-${fileKey}`, async () => {
			const decodeKey = (key: string) => {
				try {
					return atob(key)
				} catch (e) {
					return ''
				}
			}
			const decodedPath = (ctx?.filePath && ctx.filePath.trim()) || decodeKey(fileKey) || fileKey
			const project = ctx?.projectId || decodedPath.split('/')[0] || ''
			const location = ctx?.locationId

			// Prefer user-accessible endpoint
			if (project) {
				try {
					const url = location ? `/api/v2/file/${project}/${location}` : `/api/v2/file/${project}`
					const resp = await http.getAuth<{ url: string }>(url, { filekey: decodedPath })
					if (resp?.url) return resp.url
				} catch (err) {
					console.error('getFileUrl v2', err)
				}
			}

			// Admin fallback
			try {
				const resp = await http.getAuth<string>(`/api/v1/fileurl/${fileKey}`)
				if (resp) return resp
			} catch (err) {
				console.error('getFileUrl v1', err)
			}

			// Final fallback: static storage path (may require token)
			return decodedPath ? `/storage/${decodedPath}` : ''
		})
	}

	function makHumanFileSize(bytes: number) {
		const thresh = 1024
		if (Math.abs(bytes) < thresh) {
			return bytes + ' B'
		}
		const units = ['kB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB']
		let u = -1
		do {
			bytes /= thresh
			++u
		} while (Math.abs(bytes) >= thresh && u < units.length - 1)
		return bytes.toFixed(1) + ' ' + units[u]
	}

	const normalizeTarget = (val?: string | null) => (val || '').replace(/\\/g, '/').replace(/^\/+|\/+$/g, '')

	const registerUploads = (files: File[], target?: string) => {
		const now = Date.now()
		const entries = files.map((file, idx) => {
			const entry: FileUploadEntry = {
				id: `${now}-${idx}-${file.name}`,
				name: file.name,
				size: file.size,
				status: 'queued',
				progress: 0,
				target,
			}
			return entry
		})
		uploads.value = [...entries, ...uploads.value].slice(0, 30)
		return entries
	}

	const updateEntries = (entries: FileUploadEntry[], data: Partial<FileUploadEntry>) => {
		const entryIds = new Set(entries.map((e) => e.id))
		uploads.value = uploads.value.map((entry) => (entryIds.has(entry.id) ? { ...entry, ...data } : entry))
	}

	async function deleteFile(file: FileNode) {
		if (!file?.fileId || !file?.fullPath) throw new Error('Missing file data')
		const normalizedPath = file.fullPath.replace(/^\/+/, '')
		const project = normalizedPath.split('/')[0]
		if (!project) throw new Error('Missing project for delete')

		file.isDeleting = true
		try {
			const url = `/api/v2/delete/${project}/${file.fileId}?fileKey=${encodeURIComponent(normalizedPath)}`
			await http.deleteAuth(url)
			return true
		} catch (error: any) {
			throw new Error(formatError(error))
		} finally {
			file.isDeleting = false
		}
	}

	async function uploadFiles(params: {
		files: File[]
		projectId?: string | null
		locationId?: string | null
		root?: string | null
		targetPrefix?: string | null
	}) {
		const fileList = Array.from(params.files || []).filter(Boolean)
		if (!fileList.length) return

		const targetPrefix = normalizeTarget(params.targetPrefix || '')
		const baseTarget = normalizeTarget(params.root || params.locationId || params.projectId || '')
		const targetLabel = normalizeTarget([baseTarget, targetPrefix].filter(Boolean).join('/'))
		const entries = registerUploads(fileList, targetLabel || undefined)
		updateEntries(entries, { status: 'uploading', progress: 10 })

		const formData = new FormData()
		fileList.forEach((file) => formData.append('doFiles', file))

		let url = ''
		if (params.root) {
			const normalizedRoot = params.root.replace(/^\/+|\/+$/g, '')
			if (!normalizedRoot) throw new Error('Missing upload root path')
			url = `/api/v1/file/upload/${btoa(normalizedRoot)}`
		} else if (params.projectId && params.locationId) {
			url = `/api/v2/uploadfiles/${params.projectId}/${params.locationId}`
		} else if (params.projectId) {
			url = `/api/v2/uploadfiles/${params.projectId}`
		} else {
			throw new Error('Missing upload target')
		}

		if (targetPrefix) url += `?target=${encodeURIComponent(targetPrefix)}`

		try {
			await http.postAuth(url, formData)
			updateEntries(entries, { status: 'success', progress: 100, uploadedAt: Date.now() })
			return true
		} catch (error: any) {
			const errMsg = formatError(error)
			updateEntries(entries, { status: 'error', error: errMsg, progress: 0 })
			throw new Error(errMsg)
		}
	}

	return {
		previewFile,
		previewContext,
		loading,
		uploads,
		listFiles,
		listFolder,
		folderSize,
		zipFolder,
		getFileUrl,
		deleteFile,
		uploadFiles,
		makHumanFileSize,
	}

	// listFiles: async function (params: IListFilesParams) {
	// //? FILES
	// //? FILES
	// //? GET FILES
	// // async _listBBFiles({ state, dispatch }: any, project: any) {
	// // 	try {
	// // 		console.log('_listDoFiles')
	// // 		const config = await dispatch("_config")
	// // 		const resp = await axios.get(env.apiUrl + `/api/v1/storage/${project}`, config)
	// // 		return resp.data
	// // 	} catch (error: any) {
	// // 		console.error(error)
	// // 		const msg = error.response?.data?.msg || error.response?.data || error.message
	// // 		µUtils.µ_error(msg)
	// // 		return {}
	// // 	}
	// // },
	// // async _listDoFiles({ state, dispatch }: any, project: any) {
	// async _listDoFiles({ state, dispatch }: any, data: any) {
	// 	console.log('_listDoFiles', data)
	// 	// rootfolder
	// 	const base64path = btoa(data.root || '')
	// 	console.log('base64path', base64path)
	// 	try {
	// 		const config = await dispatch('_config')
	// 		let url = env.apiUrl + `/api/v2/storage/${data.projectId}`
	// 		if (data.root) url = env.apiUrl + `/api/v2/storage/root/${base64path}`
	// 		else if (data.locationId) url = env.apiUrl + `/api/v2/storage/${data.projectId}/${data.locationId}`
	// 		console.log('url', url)
	// 		const resp = await axios.get(url, config)
	// 		return resp.data
	// 	} catch (error: any) {
	// 		console.error(error)
	// 		const msg = error.response?.data?.msg || error.response?.data || error.message
	// 		µUtils.µ_error(msg)
	// 		return {}
	// 	}
	// }
	// // //? GET ONE FILE LINK
	// async _fileUrl({ state, dispatch }: any, fileKey: any) {
	// 	console.log('_fileUrl')
	// 	try {
	// 		const config = await dispatch('_config')
	// 		const resp = await axios.get(env.apiUrl + `/api/v1/fileurl/${fileKey}`, config)
	// 		console.log(resp.data)
	// 		return resp.data
	// 	} catch (error: any) {
	// 		console.error(error)
	// 		throw new Error(error?.response?.data?.msg || error?.response?.data)
	// 	}
	// }
	// //? GET ONE FILE LINK
	// async _createZipLink({ state, dispatch }: any, data: any) {
	// 	try {
	// 		console.log(data)
	// 		const config = await dispatch('_config')
	// 		const headers = { ...config.headers, foldername: data.folderName, root: data.root, comapp: data.comapp }
	// 		// const resp = await axios.get(env.apiUrl + `/api/v2/file/${data.project}/${data.fileId}`, { headers })
	// 		const resp = await axios.get(env.apiUrl + `/api/v2/zipfolder/${data.project}`, { headers })
	// 		return resp.data
	// 	} catch (error: any) {
	// 		console.error(error)
	// 		throw new Error(error?.response?.data?.msg || error?.response?.data || 'failed to download zip')
	// 	}
	// }
	// async _getDoFileLink({ state, dispatch }: any, data: any) {
	// 	try {
	// 		const config = await dispatch('_config')
	// 		const headers = { ...config.headers, fileKey: data.fileKey }
	// 		// const resp = await axios.get(env.apiUrl + `/api/v2/file/${data.project}/${data.fileId}`, { headers })
	// 		const resp = await axios.get(env.apiUrl + `/api/v2/file/${data.project}`, { headers })
	// 		return resp.data
	// 	} catch (error: any) {
	// 		console.error(error)
	// 		throw new Error(error?.response?.data?.msg || error?.response?.data)
	// 	}
	// }
	// //? DELETE ONE FILE Depriciate
	// async _deleteFile({ state, dispatch }: any, fileId: any) {
	// 	try {
	// 		const config = await dispatch('_config')
	// 		const resp = await axios.delete(env.apiUrl + `/api/v1/file/delete/${fileId}`, config)
	// 		µUtils.µ_success('file deleted')
	// 		return
	// 	} catch (error: any) {
	// 		console.error(error)
	// 		const msg = error.response?.data?.msg || error.response?.data || error.message
	// 		µUtils.µ_error(msg)
	// 		throw new Error(error?.response?.data?.msg || error?.response?.data)
	// 	}
	// }
	// //? DELETE ONE FILE DO
	// async _deleteDoFile({ state, dispatch }: any, data: any) {
	// 	try {
	// 		console.log(data)
	// 		const config = await dispatch('_config')
	// 		const resp = await axios.delete(
	// 			env.apiUrl + `/api/v2/delete/${data.project}/${data.fileId}?fileKey=${data.fileKey}`,
	// 			config,
	// 		)
	// 		// console.log(resp.data);
	// 		µUtils.µ_success('file deleted')
	// 		return
	// 	} catch (error: any) {
	// 		console.error(error)
	// 		const msg = error.response?.data?.msg || error.response?.data || error.message
	// 		µUtils.µ_error(msg)
	// 		throw new Error(error?.response?.data?.msg || error?.response?.data)
	// 	}
	// }
	// //? HISTORY FOR ONE FILE
	// async _fileHistory({ dispatch }: any, data: any) {
	// 	try {
	// 		const config = await dispatch('_config')
	// 		const resp = await axios.get(env.apiUrl + `/api/v1/filehistory/${data.project}/${data.fileId}`, config)
	// 		return resp.data
	// 	} catch (error: any) {
	// 		console.error(error)
	// 		const msg = error.response?.data?.msg || error.response?.data || error.message
	// 		µUtils.µ_error(msg)
	// 		throw new Error(error?.response?.data?.msg || error?.response?.data)
	// 	}
	// }
})
